/**
 * Custom error classes for structured error handling across the app.
 * Each error carries a code (programmatic), userMessage (user-facing),
 * and context (structured data for Sentry).
 */

class AppError extends Error {
  constructor(message, { code, userMessage, context } = {}) {
    super(message);
    this.name = this.constructor.name;
    this.code = code || 'APP_ERROR';
    this.userMessage = userMessage || message;
    this.context = context || {};
  }
}

class DocumentNotFoundError extends AppError {
  constructor(documentId, context = {}) {
    super(`Document not found: ${documentId}`, {
      code: 'DOCUMENT_NOT_FOUND',
      userMessage: 'Il documento richiesto non è stato trovato.',
      context: { documentId, ...context }
    });
  }
}

class QuestionsNotFoundError extends AppError {
  constructor(documentId, context = {}) {
    super(`No questions found for document: ${documentId}`, {
      code: 'QUESTIONS_NOT_FOUND',
      userMessage: 'Le domande per questo documento non sono ancora disponibili.',
      context: { documentId, ...context }
    });
  }
}

class ApiTimeoutError extends AppError {
  constructor(endpoint, timeoutMs, context = {}) {
    super(`API timeout after ${timeoutMs}ms on ${endpoint}`, {
      code: 'API_TIMEOUT',
      userMessage: 'Il server sta impiegando troppo tempo. Riprova tra qualche momento.',
      context: { endpoint, timeoutMs, ...context }
    });
  }
}

class InvalidResponseError extends AppError {
  constructor(endpoint, reason, context = {}) {
    super(`Invalid response from ${endpoint}: ${reason}`, {
      code: 'INVALID_RESPONSE',
      userMessage: 'La risposta dal server non è nel formato atteso.',
      context: { endpoint, reason, ...context }
    });
  }
}

class FeedbackError extends AppError {
  constructor(reason, context = {}) {
    super(`Feedback error: ${reason}`, {
      code: 'FEEDBACK_ERROR',
      userMessage: 'Non è stato possibile ottenere il feedback. Riprova.',
      context
    });
  }
}

class ContentValidationError extends AppError {
  constructor(contentType, reason, context = {}) {
    super(`Content validation failed for ${contentType}: ${reason}`, {
      code: 'CONTENT_VALIDATION_ERROR',
      userMessage: 'I contenuti generati non hanno superato la validazione di qualità.',
      context: { contentType, reason, ...context }
    });
  }
}

class AuthenticationError extends AppError {
  constructor(context = {}) {
    super('User not authenticated', {
      code: 'AUTH_ERROR',
      userMessage: 'Sessione scaduta. Effettua nuovamente il login.',
      context
    });
  }
}

export {
  AppError,
  DocumentNotFoundError,
  QuestionsNotFoundError,
  ApiTimeoutError,
  InvalidResponseError,
  FeedbackError,
  ContentValidationError,
  AuthenticationError
};
